import { type RefObject, useEffect } from 'react'

// One observer for every card on the artboard; the class to toggle is remembered per element.
let observer: IntersectionObserver | null = null
const classNameByElement = new WeakMap<Element, string>()

const getObserver = (): IntersectionObserver => {
  if (observer) {
    return observer
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const className = classNameByElement.get(entry.target)

        if (className) {
          entry.target.classList.toggle(className, !entry.isIntersecting)
        }
      }
    },
    // root is the viewport; the margin keeps cards painted before they scroll in
    { rootMargin: '50%' }
  )

  return observer
}

/**
 * Marks the card with `className` while it is far outside the viewport (OCT-5072).
 * The class sets `visibility: hidden`, which keeps layout, connector lines and
 * getBoundingClientRect intact and only skips painting and hit-testing. On a map with
 * 500+ pages that is what makes pan and zoom affordable in WebKit: without it every
 * step re-rasterizes ~100k elements. Off in print/export renders and for DnD overlays.
 */
export const useOffscreenCulling = (
  ref: RefObject<HTMLElement | null>,
  className: string,
  enabled: boolean
): void => {
  useEffect(() => {
    const element = ref.current

    if (!enabled || !element || typeof IntersectionObserver === 'undefined') {
      return
    }

    classNameByElement.set(element, className)
    const io = getObserver()
    io.observe(element)

    return () => {
      io.unobserve(element)
      element.classList.remove(className)
      classNameByElement.delete(element)
    }
  }, [ref, className, enabled])
}
